import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiCheck, FiX } from 'react-icons/fi'
import toast from 'react-hot-toast'
import AuthModal from './AuthModal'
import { useAuth } from '../contexts/AuthContext'
import ComparisonTable from './ComparisonTable'
import Card from './ui/Card'
import SectionTitle from './ui/SectionTitle'
import Button from './ui/Button'
import Reveal from './ui/Reveal'
import { useUserRealtimeData } from '../hooks/useUserRealtimeData'
import DeviceRequirementModal from './DeviceRequirementModal'
import { getAuth } from 'firebase/auth'
import { hideProducts } from '../config/featureFlags'
import { PRICING_PLANS } from '../config/plans'
import { ANNUAL_MARKETING_TAGLINE } from '../utils/subscriptionPlan'
import { apiUrl } from '../config/api'

const Pricing = () => {
  const navigate = useNavigate()
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showDeviceRequirementModal, setShowDeviceRequirementModal] = useState(false)
  const { user } = useAuth()
  const { profile, loading: userDataLoading } = useUserRealtimeData()

  const hasDevice =
    Boolean(profile?.deviceOwned) ||
    Boolean(profile?.deviceClaimed)

  useEffect(() => {
    if (!user) return
    if (userDataLoading) return
    if (hasDevice) return
    if (!profile?.asaasDevicePaymentId) return
    if (!profile?.devicePurchaseStatus) return

    const statusesDone = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH']
    const statusUpper = String(profile?.devicePurchaseStatus || '').toUpperCase()
    if (statusesDone.includes(statusUpper)) return

    let alive = true
    ;(async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken()
        if (!token) return
        const resp = await fetch(apiUrl('/api/device/sync'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        const raw = await resp.text()
        const data = (() => {
          try { return JSON.parse(raw) } catch { return null }
        })()
        if (!alive) return
        if (!resp.ok || !data?.ok) return
      } catch {
        // silencioso
      }
    })()
    return () => {
      alive = false
    }
  }, [user, userDataLoading, hasDevice, profile?.asaasDevicePaymentId, profile?.devicePurchaseStatus])

  const goToCheckout = (planId) => {
    navigate(`/checkout/${planId}`)
  }

  const handleSelectPlan = (planId) => {
    if (planId === 'gratuito') {
      if (!user) setShowAuthModal(true)
      else toast.success('Plano gratuito ativo após cadastro!')
      return
    }

    setSelectedPlan(planId)
    if (!user) {
      setShowAuthModal(true)
    } else if (userDataLoading) {
      toast.error('Carregando seu status...')
      setSelectedPlan(null)
    } else if (!hasDevice) {
      setShowDeviceRequirementModal(true)
    } else {
      goToCheckout(planId)
    }
  }

  return (
    <>
      <section id="pricing" className="section-padding bg-gray-50 dark:bg-gray-800/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34, 198, 94, 0.06) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />

        <div className="container-custom relative z-10">
          <Reveal direction="up">
            <SectionTitle
              title="Nossos Planos"
              subtitle="Escolha o Ideal"
              align="center"
            />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-8 sm:mb-10 w-full min-w-0 max-w-6xl mx-auto">
            {PRICING_PLANS.map((plan, index) => {
              const Icon = plan.icon
              return (
                <Reveal key={plan.id} direction="up" delay={index * 100}>
                  <Card
                    hover
                    padding="md"
                    glow={plan.highlight}
                    className={`
                      relative flex flex-col h-full transition-all duration-300 !p-5 sm:!p-6 lg:!p-5 xl:!p-6
                      ${plan.highlight
                        ? 'border-2 border-accent/50 ring-2 ring-accent/15 dark:ring-accent/25 shadow-lg dark:shadow-xl'
                        : ''
                      }
                    `}
                  >
                    {plan.badge && (
                      <div className="absolute -top-2.5 right-4 lg:right-3 bg-accent text-white px-3 py-1 rounded-full text-[10px] font-extrabold tracking-widest uppercase shadow-md shadow-accent/30">
                        {plan.badge}
                      </div>
                    )}

                    <div className="flex items-center gap-2.5 mb-3">
                      <div
                        className={`
                          w-10 h-10 lg:w-11 lg:h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300
                          ${plan.highlight
                            ? 'bg-accent text-white shadow-md shadow-accent/30'
                            : 'bg-accent/10 dark:bg-accent/20 text-accent'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                        {plan.name}
                      </h3>
                    </div>

                    <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                      {plan.price === 'R$ 0' && (
                        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-0.5">A partir de</p>
                      )}
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl lg:text-[1.5rem] font-bold text-gray-900 dark:text-white">
                          {plan.price}
                        </span>
                        {plan.period && (
                          <span className="text-base text-gray-600 dark:text-gray-400">
                            {plan.period}
                          </span>
                        )}
                      </div>
                      {plan.annualHint && (
                        <p className="mt-1.5 text-sm font-semibold text-accent leading-snug">
                          Anual: {plan.annualHint} ({plan.annualPrice}/ano)
                        </p>
                      )}
                    </div>

                    <ul className="space-y-2 mb-4 flex-grow">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          {feature.included ? (
                            <>
                              <FiCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5 font-bold check-pop" />
                              <span className="text-gray-700 dark:text-gray-300 text-sm lg:text-base leading-snug">
                                {feature.text}
                                {feature.trial && (
                                  <span className="ml-1.5 inline-block bg-accent/20 dark:bg-accent/30 text-accent px-2 py-0.5 rounded-full text-xs font-bold">
                                    30 dias Trial
                                  </span>
                                )}
                              </span>
                            </>
                          ) : (
                            <>
                              <FiX className="w-5 h-5 text-gray-400 dark:text-gray-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-400 dark:text-gray-600 line-through text-sm lg:text-base leading-snug">
                                {feature.text}
                              </span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSelectPlan(plan.id)}
                      variant={plan.highlight ? 'primary' : 'secondary'}
                      size="md"
                      className="w-full mt-auto !min-h-[34px] !py-1.5 !px-3 !text-[16px] !rounded-xl"
                    >
                      {plan.price === 'R$ 0' ? 'Começar Grátis' : 'Assinar Plano'}
                    </Button>
                  </Card>
                </Reveal>
              )
            })}
          </div>

          <Reveal direction="up" delay={300}>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              Planos pagos: escolha cobrança mensal ou {ANNUAL_MARKETING_TAGLINE.toLowerCase()} na página de pagamento.
            </p>
            <ComparisonTable />
          </Reveal>
        </div>
      </section>

      {showDeviceRequirementModal && user && selectedPlan && (
        <DeviceRequirementModal
          onClose={() => setShowDeviceRequirementModal(false)}
          onConfirmed={() => {
            setShowDeviceRequirementModal(false)
            goToCheckout(selectedPlan)
          }}
          onNoDevice={() => {
            setShowDeviceRequirementModal(false)
            setSelectedPlan(null)
            if (!hideProducts) {
              document.getElementById('products')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            } else {
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }}
        />
      )}

      {showAuthModal && !user && (
        <AuthModal
          onClose={() => {
            setShowAuthModal(false)
          }}
        />
      )}
    </>
  )
}

export default Pricing
